import axios from "axios";
import { API_URL } from "configs";

const CACHE_TTL_MS = 45_000;

let dashboardCache = {
  data: null,
  fetchedAt: 0,
  promise: null,
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export function buildDashboardSummaryItems(counts, total) {
  return [
    {
      id: "total",
      label: "Total",
      value: total,
      caption: "All PAF",
      tone: "primary",
      icon: "clipboard",
    },
    {
      id: "completed",
      label: "Completed",
      value: counts.completed ?? 0,
      caption: "Completed PAF",
      tone: "success",
      icon: "check",
    },
    {
      id: "pending",
      label: "Pending",
      value: counts.pending ?? 0,
      caption: "Pending PAF",
      tone: "pending",
      icon: "clock",
    },
    {
      id: "delayed",
      label: "Delayed",
      value: counts.delayed ?? 0,
      caption: "Delayed PAF",
      tone: "warning",
      icon: "hourglass",
    },
  ];
}

export function localIsoDate(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseIsoDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function isSameLocalDay(left, right) {
  const a = parseIsoDate(left);
  const b = parseIsoDate(right);
  if (!a || !b) return false;
  return a.toDateString() === b.toDateString();
}

export function formatUpcomingPafDate(iso, todayIso) {
  const date = parseIsoDate(iso);
  if (!date) return iso || "";

  const options = { day: "numeric", month: "long" };
  const today = parseIsoDate(todayIso);
  if (today && date.getFullYear() !== today.getFullYear()) {
    options.year = "numeric";
  }

  return date.toLocaleDateString("en-GB", options);
}

function normalizeScheduleEvents(events) {
  if (!Array.isArray(events)) return [];

  return events
    .map((event, index) => {
      const date = String(event?.date || "").slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;

      const title = String(event?.title || event?.name || "").trim();
      if (!title) return null;

      return {
        id: String(event?.id || `${date}-${index}`),
        date,
        title,
        name: title,
        frequency: String(event?.frequency || "").trim(),
        type: String(event?.type || "paf_generation"),
        appraisal_type: event?.appraisal_type || null,
        designation: event?.designation || null,
        designations: Array.isArray(event?.designations) ? event.designations : [],
      };
    })
    .filter(Boolean);
}

function normalizeDashboardPayload(payload) {
  const completed = Number(payload?.completed ?? 0) || 0;
  const pending = Number(payload?.pending ?? 0) || 0;
  const delayed = Number(payload?.delayed ?? 0) || 0;
  const total =
    Number(payload?.total ?? completed + pending + delayed) ||
    completed + pending + delayed;
  const todayMatch = String(payload?.today || "").match(/^(\d{4}-\d{2}-\d{2})/);
  const today = todayMatch ? todayMatch[1] : localIsoDate();

  return {
    counts: { completed, pending, delayed },
    total,
    today,
    events: normalizeScheduleEvents(payload?.events),
    upcoming: normalizeScheduleEvents(payload?.upcoming),
    summaryItems: buildDashboardSummaryItems(
      { completed, pending, delayed },
      total
    ),
  };
}

/**
 * GET /api/dashboard — single aggregated PAF summary for the logged-in user.
 * Cached for ~45s to avoid refetching on remounts shortly after login.
 */
export async function fetchDashboardSummary({ force = false } = {}) {
  const now = Date.now();

  if (
    !force &&
    dashboardCache.data &&
    now - dashboardCache.fetchedAt < CACHE_TTL_MS
  ) {
    return dashboardCache.data;
  }

  if (!force && dashboardCache.promise) {
    return dashboardCache.promise;
  }

  dashboardCache.promise = axios
    .get(`${API_URL}dashboard`, {
      headers: authHeaders(),
    })
    .then((response) => {
      const result = normalizeDashboardPayload(response?.data);
      dashboardCache = {
        data: result,
        fetchedAt: Date.now(),
        promise: null,
      };
      return result;
    })
    .catch((error) => {
      dashboardCache.promise = null;
      throw error;
    });

  return dashboardCache.promise;
}

export function clearDashboardSummaryCache() {
  dashboardCache = { data: null, fetchedAt: 0, promise: null };
}

export function getDashboardReportingPeriod(date = new Date()) {
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}
